import { Box, Dialog, DialogActions, DialogContent, useTheme, IconButton, Stack, Tooltip, Typography, useMediaQuery, Grid, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { useSelector } from "react-redux";
import { Form, FormikProvider, useFormik } from "formik";
import { toast } from "react-toastify";

import { createGeofenceSchema } from "../../../schemas/geofence.schema";
import { GeofenceTypes } from "../../../types/geofence.types";
import { RootState } from "../../../store";
import { GeofenceService } from "../../../services/geofence.service";

interface Props {
    defaultCoordinates: GeofenceTypes.GeofenceCoordinate[];
    editGeofence?: GeofenceTypes.Model | null;
    visible: boolean;
    onClose: () => void;
}

export const GeofenceForm: React.FC<Props> = ({ visible, defaultCoordinates, editGeofence, onClose }) => {
    const theme = useTheme();
    const { geoPhones, loading } = useSelector((state: RootState) => state.geofence);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const formik = useFormik<GeofenceTypes.CreateGeofenceParams>({
        validationSchema: createGeofenceSchema,
        initialValues: editGeofence ? {
            description: editGeofence.description,
            coordinates: editGeofence.coordinates,
            phoneNumber: editGeofence.phoneNumber,
        } as GeofenceTypes.CreateGeofenceParams : {
            coordinates: defaultCoordinates,
        } as GeofenceTypes.CreateGeofenceParams,
        onSubmit: async (geofence) => {
            if (editGeofence) {
                await GeofenceService.updateGeofence({
                    coordinates: geofence?.coordinates!,
                    description: geofence?.description!,
                    id: editGeofence?.id!,
                    phoneNumber: geofence.phoneNumber,
                } as GeofenceTypes.UpdateGeofenceParams, (isValid, msg) => {
                    if (!isValid) {
                        toast(
                            msg,
                            {
                                autoClose: 1500,
                                type: 'error',
                            }
                        );
                        return;
                    }
    
                    toast(
                        'Geofence successfully updated.',
                        {
                            autoClose: 1500,
                            type: 'success',
                        },
                    );
                });

            } else {
                await GeofenceService.createGeofence(geofence, (isValid, msg) => {
                    if (!isValid) {
                        toast(
                            msg,
                            {
                                autoClose: 1500,
                                type: 'error',
                            }
                        );
                        return;
                    }
    
                    toast(
                        'Geofence successfully created, now you can just drag and change the geofence area.',
                        {
                            autoClose: 1500,
                            type: 'success',
                        },
                    );
                });
            }

            resetForm();
            onClose();
        },
    });

    const { handleSubmit, errors, resetForm, getFieldProps } = formik;

    return (
        <Dialog 
            open={visible} 
            fullWidth={isSmallScreen}
            maxWidth={'lg'}
            scroll={'paper'}
        >
            <Box sx={{ borderBottom: 1, p: 2, borderColor: 'divider' }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Typography fontWeight={400} fontSize={20} textAlign={'left'}>
                        {editGeofence ? 'Edit Geofence' : 'Add a new Geofence'}
                    </Typography>
                    <Tooltip title="Close form">
                        <IconButton
                            onClick={onClose}
                            component="span"
                            sx={{ mr: 2, height: 30, width: 30, alignSelf: 'center' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <FormikProvider value={formik}>
                <Form noValidate onSubmit={handleSubmit}>
                    <DialogContent dividers sx={{ minWidth: isSmallScreen ? '90%' : 700 }}>
                        <Grid container spacing={2}>
                            <Grid item md={12} sm={12} xs={24}>
                                <TextField
                                    label="Geofence Name"
                                    placeholder="Please type a short name for the geofence"
                                    fullWidth
                                    required
                                    variant='outlined'
                                    error={Boolean(errors.description)}
                                    helperText={errors.description}
                                    {...getFieldProps('description')}
                                />
                            </Grid>
                            <Grid item md={12} sm={12} mt={2} xs={24}>
                                <TextField
                                    label="Phone"
                                    placeholder="Please type the phone number"
                                    fullWidth
                                    required
                                    variant='outlined'
                                    error={Boolean(errors.phoneNumber)}
                                    helperText={errors.phoneNumber}
                                    {...getFieldProps('phoneNumber')}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={loading.createGeofence || loading.updateGeofence}
                            fullWidth={isSmallScreen}
                        >
                            Confirm
                        </LoadingButton>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    )
};